import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';

import { useGenericSearch } from '@/hooks/useGenericSearch';
import { useJobProgress } from '@/hooks/useJobProgress';
import { Job, ResourceType } from '@/types';
import { sToMs } from '@/utils/date.util';

const CAPABILITY_DESCRIPTIONS: Record<string, string> = {
  whois: 'Checking domain registration',
  subdomain: 'Discovering subdomains',
  portscan: 'Scanning open ports',
  edgar: 'Analyzing SEC filings',
  ip: 'Mapping IP addresses',
  cidr: 'Analyzing network ranges',
  nuclei: 'Running vulnerability scans',
  'github-repository': 'Scanning GitHub repositories',
  secrets: 'Analyzing for exposed secrets',
  ssh: 'Checking SSH configurations',
  website: 'Analyzing web applications',
};

const JobProgressBar = () => {
  const [currentJobIndex, setCurrentJobIndex] = useState(0);
  const { showProgress, stopProgress } = useJobProgress();

  // Only query if we should show progress
  const { data: runningJobsData } = useGenericSearch(
    {
      query: 'status:JR',
      label: ResourceType.Job,
    },
    {
      refetchInterval: sToMs(10),
      enabled: showProgress,
    }
  );

  const { data: queuedJobsData } = useGenericSearch(
    {
      query: 'status:JQ',
      label: ResourceType.Job,
    },
    {
      refetchInterval: sToMs(10),
      enabled: showProgress,
    }
  );

  const runningJobs = useMemo(() => {
    return (runningJobsData?.jobs || []) as Job[];
  }, [runningJobsData]);

  const queuedJobs = useMemo(() => {
    return (queuedJobsData?.jobs || []) as Job[];
  }, [queuedJobsData]);

  const currentJob = useMemo(() => {
    if (!runningJobs?.length) return null;
    return runningJobs[currentJobIndex % runningJobs.length];
  }, [runningJobs, currentJobIndex]);

  useEffect(() => {
    if (!runningJobs?.length || runningJobs.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentJobIndex(prev => (prev + 1) % runningJobs.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [runningJobs]);

  // Auto-hide progress when no more jobs
  useEffect(() => {
    if (showProgress && runningJobs.length === 0 && queuedJobs.length === 0) {
      // Add a small delay to ensure all jobs are really done
      const timeout = setTimeout(() => {
        stopProgress();
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [showProgress, runningJobs.length, queuedJobs.length, stopProgress]);

  // Don't render if not showing progress or no jobs
  if (!showProgress || (runningJobs.length === 0 && queuedJobs.length === 0)) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        className="border-chariot-stroke bg-chariot-background-primary fixed inset-x-0 bottom-0 left-[100px] z-50 border-t-2"
      >
        <div className="container mx-auto p-4">
          <div className="flex items-center gap-6">
            <div className="flex flex-col gap-1">
              {currentJob && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">
                    {CAPABILITY_DESCRIPTIONS[currentJob.source] ||
                      currentJob.source}
                  </span>
                  <span className="text-chariot-text-secondary text-sm">
                    {currentJob.dns}
                  </span>
                </div>
              )}
              <div className="text-chariot-text-secondary flex gap-4 text-sm">
                <span>{runningJobs.length} Running</span>
                <span>{queuedJobs.length} Queued</span>
              </div>
            </div>

            <div className="bg-chariot-background-secondary relative h-2 w-64 overflow-hidden rounded-full">
              <motion.div
                className="bg-chariot-brand-primary absolute h-full"
                animate={{
                  x: ['-100%', '400%'],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  repeatType: 'loop',
                }}
                style={{ width: '30%' }}
              />
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default JobProgressBar;
